import { createContext, useContext, useEffect, useState } from "react";
import { api, setToken, clearToken, getToken } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Two-step 2FA challenge - backend hands back a 5-minute session token
    // that the caller must swap via `verifyTwoFactor` for a real access token.
    // We surface a distinct shape so Login.jsx can render its 2FA step.
    if (data.two_factor_required) {
      return { two_factor_required: true, session_token: data.session_token };
    }
    setToken(data.access_token);
    setUser(data.user);
    return { user: data.user };
  };

  const verifyTwoFactor = async (session_token, code) => {
    const { data } = await api.post("/auth/2fa/verify-login", { session_token, code });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  // Called from Settings after a user enables OR disables 2FA so `user` state
  // reflects the change immediately (guards the 2FA card visibility, etc.).
  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, verifyTwoFactor, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
